import { inject } from 'vue'
import { RequestService } from './RequestService'
import { buildContentLibraryDownloadFileName } from '../utils/contentLibraryHelpers'

/**
 * Frontend service for Content Library brandable template preview and
 * lifecycle operations.
 *
 * Communicates with the backend utility gateway endpoints:
 *   POST /api/v1/content-library/preview-template
 *   POST /api/v1/content-library/cached-document-download
 *   POST /api/v1/content-library/regenerate-cache
 *   GET  /api/v1/content-library/cache-inspect/:itemId
 *   GET  /api/v1/content-library/cache-inspect-agencies
 *   GET  /api/v1/content-library/cache-inspect-by-agency/:agencyId
 *   POST /api/v1/content-library/regenerate-agency-cache
 */
export const ContentLibraryTemplateService = () => {
  const functionsBaseUrl = inject('functionsBaseUrl')
  const { post, simpleGet } = RequestService()
  const contentTypesByFormat = {
    pdf: 'application/pdf',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
  }

  /**
   * Render a branded preview from a local File without persisting to Storage.
   * The canonical original is uploaded when the admin saves the item.
   *
   * @param {File} file - The template file to preview
   * @param {Object} branding - Agency branding context
   * @param {string} [branding.agencyId] - Agency ID for server-side logo resolution
   * @param {string} [branding.logoSize]
   * @param {string} [branding.whitelabelColor]
   * @returns {Promise<{ brandingEngine: string, documentFileName: string, reviewBlob: Blob, reviewFileFormat: string }>}
   */
  const previewTemplate = async (file, branding = {}) => {
    const formData = new FormData()
    formData.append('template', file)

    formData.append('agencyId', branding.agencyId || '')
    formData.append('logoSize', branding.logoSize || '')
    formData.append('whitelabelColor', branding.whitelabelColor || '')

    const url = `${functionsBaseUrl}/utilityGateway/api/v1/content-library/preview-template`

    const response = await post('contentLibraryPreviewTemplate', url, formData)

    const result = response.data
    if (!result.success || !result.data?.brandingEngine) {
      throw new Error('Preview did not return a valid branding engine key')
    }

    const base64 = result.data.reviewFile
    const byteArray = Uint8Array.from(atob(base64), c => c.charCodeAt(0))
    const reviewBlob = new Blob([byteArray], { type: result.data.reviewFileType || 'application/pdf' })

    return {
      brandingEngine: result.data.brandingEngine,
      documentFileName: result.data.documentFileName,
      reviewBlob,
      reviewFileFormat: result.data.reviewFileFormat || 'pdf'
    }
  }

  /**
   * Download a cached Content Library document.
   *
   * Renderer-agnostic: the backend serves whatever the cache pipeline produced.
   * Requires the item's cacheStatus === 'processed'.
   *
   * @param {Object} params
   * @param {string} params.itemId - RTDB key of the Content Library item.
   * @param {string} params.format - Output format (pdf | docx | xlsx | pptx).
   * @param {Object} params.item - Content Library item, used to derive the browser download filename.
   * @param {boolean} [params.whiteLabel=false] - Serve the agency-branded variant.
   */
  const downloadCachedContentLibraryDocument = async ({ itemId, format, item, whiteLabel = false } = {}) => {
    const response = await post(
      'contentLibraryCachedDocumentDownload',
      `${functionsBaseUrl}/utilityGateway/api/v1/content-library/cached-document-download`,
      { itemId, format, whiteLabel },
      { responseType: 'arraybuffer' }
    )
    const blob = new Blob([response.data], { type: contentTypesByFormat[format] || 'application/octet-stream' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', buildContentLibraryDownloadFileName(item, { format }))
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
  }

  /**
   * Admin-only: trigger a manual cache regeneration for a Content Library item.
   * Backend mirrors the trigger lifecycle (pending → processing → processed/failed)
   * so the existing badge polling reflects progress without extra wiring.
   *
   * @param {string} itemId - RTDB key of the Content Library item.
   * @returns {Promise<Object>} backend summary payload.
   */
  const regenerateContentLibraryItemCache = async (itemId) => {
    const response = await post(
      'contentLibraryRegenerateCache',
      `${functionsBaseUrl}/utilityGateway/api/v1/content-library/regenerate-cache`,
      { itemId }
    )
    return response.data
  }

  /**
   * Admin-only: fetch the per-agency cache existence map for an item.
   * Used to populate the inspector modal in the backoffice.
   *
   * @param {string} itemId
   * @returns {Promise<Object>} backend payload with per-agency `formats` carrying `defaultExists` + `whitelabelExists`.
   */
  const inspectContentLibraryItemCache = async (itemId) => {
    const response = await simpleGet(
      'contentLibraryCacheInspect',
      `${functionsBaseUrl}/utilityGateway/api/v1/content-library/cache-inspect/${encodeURIComponent(itemId)}`
    )
    return response.data
  }

  const listContentLibraryCacheInspectAgencies = async () => {
    const response = await simpleGet(
      'contentLibraryCacheInspectAgencies',
      `${functionsBaseUrl}/utilityGateway/api/v1/content-library/cache-inspect-agencies`
    )
    return response.data?.data || []
  }

  const inspectContentLibraryCacheByAgency = async (agencyId) => {
    const response = await simpleGet(
      'contentLibraryCacheInspectByAgency',
      `${functionsBaseUrl}/utilityGateway/api/v1/content-library/cache-inspect-by-agency/${encodeURIComponent(agencyId)}`
    )
    return response.data?.data || null
  }

  /**
   * Admin-only: enqueue cache regeneration for a single (item, agency) pair.
   * Returns { success, status: 'queued', queuedJobs, enqueuedAt }.
   * Missing logo is handled asynchronously (see cache inspector / job outcome).
   */
  const regenerateContentLibraryAgencyCache = async ({ itemId, agencyId }) => {
    const response = await post(
      'contentLibraryRegenerateAgencyCache',
      `${functionsBaseUrl}/utilityGateway/api/v1/content-library/regenerate-agency-cache`,
      { itemId, agencyId }
    )
    return response.data
  }

  return {
    previewTemplate,
    downloadCachedContentLibraryDocument,
    regenerateContentLibraryItemCache,
    inspectContentLibraryItemCache,
    listContentLibraryCacheInspectAgencies,
    inspectContentLibraryCacheByAgency,
    regenerateContentLibraryAgencyCache
  }
}
